import type { Connection } from '@solana/web3.js';
import type { TxEngineLogger } from '../../tx-engine/config.js';
import { isValidTransactionTraceId } from '../../tx-engine/diagnostics/lifecycle.js';
import {
  type AcceptedBlockmetaSnapshot,
  BLOCKMETA_STREAM_MAX_AGE_MS,
  type LatestBlockmetaStore,
} from '../blockmeta/index.js';
import {
  BLOCKHASH_LOCAL_CACHE_TTL_MS,
  BLOCKHASH_STREAM_COMMITMENT,
  type BlockhashResolverMode,
} from './constants.js';

type RpcCommitment = 'processed' | 'confirmed' | 'finalized';
type FallbackReason =
  | 'hit'
  | 'missing'
  | 'stream_too_old'
  | 'invalid'
  | 'expiry_required'
  | 'observe_mode'
  | 'off_mode'
  | 'rpc_failure';

export interface RecentBlockhash {
  readonly blockhash: string;
  readonly source: 'local_cache' | 'stream' | 'rpc';
  readonly reason: FallbackReason;
  readonly commitment: RpcCommitment;
  readonly cachedAtMs: number;
  readonly chainTimestampSec?: number;
  readonly lastValidBlockHeight?: number;
}

export interface BlockhashWithRpcExpiry {
  readonly blockhash: string;
  readonly lastValidBlockHeight: number;
  readonly source: 'rpc';
  readonly reason: FallbackReason;
  readonly commitment: RpcCommitment;
  readonly cachedAtMs: number;
}

export class BlockhashUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BlockhashUnavailableError';
  }
}

export interface BlockhashResolverOptions {
  readonly resolveMode: () => BlockhashResolverMode;
  readonly store: LatestBlockmetaStore;
  readonly logger: TxEngineLogger;
  readonly rpcCommitment?: RpcCommitment;
  readonly now?: () => number;
  readonly monotonicNow?: () => number;
}

export interface BlockhashResolutionContext {
  readonly traceId?: string;
  readonly suppressRpcFallbackWarning?: boolean;
}

export class BlockhashResolver {
  private readonly resolveMode: () => BlockhashResolverMode;
  private readonly store: LatestBlockmetaStore;
  private readonly logger: TxEngineLogger;
  private readonly rpcCommitment: RpcCommitment;
  private readonly now: () => number;
  private readonly monotonicNow: () => number;
  private localCache: RecentBlockhash | null = null;

  constructor(options: BlockhashResolverOptions) {
    this.resolveMode = options.resolveMode;
    this.store = options.store;
    this.logger = options.logger;
    this.rpcCommitment = options.rpcCommitment ?? 'confirmed';
    this.now = options.now ?? Date.now;
    this.monotonicNow = options.monotonicNow ?? (() => performance.now());
  }

  resolveRecentBlockhash(
    connection: Connection,
    context?: BlockhashResolutionContext,
  ): Promise<RecentBlockhash> {
    const nowMs = this.now();
    const mode = this.resolveMode();
    const cached = this.localCache;
    if (mode === 'stream') {
      const nowMonoMs = this.monotonicNow();
      const stream = this.usableStreamSnapshot(nowMonoMs);
      if (stream.usable) {
        const result = this.cacheStreamSnapshot(stream.snapshot, nowMs);
        this.recordSource('stream', 'hit', result);
        return Promise.resolve(result);
      }
      this.logStreamFallback(stream.reason, stream.snapshot, nowMonoMs, context);
      const traceId = isValidTransactionTraceId(context?.traceId) ? context.traceId : undefined;
      this.logger.warn('blockchain.blockhash.stream_unavailable', {
        commitment: BLOCKHASH_STREAM_COMMITMENT,
        reason: stream.reason,
        ...(stream.snapshot === null
          ? {}
          : {
              ageMs: String(nowMonoMs - stream.snapshot.receivedAtMonoMs),
              maxAgeMs: String(BLOCKMETA_STREAM_MAX_AGE_MS),
            }),
        ...(traceId === undefined ? {} : { traceId }),
      });
      return Promise.reject(
        new BlockhashUnavailableError(`Streamed blockhash unavailable: ${stream.reason}`),
      );
    }

    if (cached !== null && this.isUsableLocalCache(cached, nowMs)) {
      this.recordSource('local_cache', 'hit', cached);
      return Promise.resolve({ ...cached, source: 'local_cache', reason: 'hit' });
    }

    if (mode === 'observe') {
      this.observeStreamState(nowMs);
      return this.resolveRpcRecent(connection, 'observe_mode', context);
    }

    return this.resolveRpcRecent(connection, 'off_mode', context);
  }

  async resolveBlockhashWithRpcExpiry(
    connection: Connection,
    context?: BlockhashResolutionContext,
  ): Promise<BlockhashWithRpcExpiry> {
    const nowMs = this.now();
    if (this.resolveMode() === 'observe') {
      this.observeStreamState(nowMs);
    }
    try {
      const result = await connection.getLatestBlockhash(this.rpcCommitment);
      const resolved: BlockhashWithRpcExpiry = {
        blockhash: result.blockhash,
        lastValidBlockHeight: result.lastValidBlockHeight,
        source: 'rpc',
        reason: 'expiry_required',
        commitment: this.rpcCommitment,
        cachedAtMs: nowMs,
      };
      this.recordSource('rpc', 'expiry_required', resolved);
      return resolved;
    } catch (err: unknown) {
      this.recordRpcFailure('expiry_required', err, context);
      throw new BlockhashUnavailableError(
        `RPC blockhash lookup failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  private async resolveRpcRecent(
    connection: Connection,
    reason: FallbackReason,
    context?: BlockhashResolutionContext,
  ): Promise<RecentBlockhash> {
    const nowMs = this.now();
    try {
      const result = await connection.getLatestBlockhash(this.rpcCommitment);
      const resolved: RecentBlockhash = {
        blockhash: result.blockhash,
        lastValidBlockHeight: result.lastValidBlockHeight,
        source: 'rpc',
        reason,
        commitment: this.rpcCommitment,
        cachedAtMs: nowMs,
      };
      this.localCache = resolved;
      this.recordSource('rpc', reason, resolved);
      return resolved;
    } catch (err: unknown) {
      this.recordRpcFailure(reason, err, context);
      throw new BlockhashUnavailableError(
        `RPC blockhash lookup failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  private usableStreamSnapshot(nowMonoMs: number):
    | { readonly usable: true; readonly snapshot: AcceptedBlockmetaSnapshot }
    | {
        readonly usable: false;
        readonly reason: FallbackReason;
        readonly snapshot: AcceptedBlockmetaSnapshot | null;
      } {
    const snapshot = this.store.getLatest();
    if (snapshot === null) {
      const rejected = this.store.getLastRejected();
      return {
        usable: false,
        reason: rejected !== null ? 'invalid' : 'missing',
        snapshot: null,
      };
    }

    const receivedAgeMs = nowMonoMs - snapshot.receivedAtMonoMs;
    if (receivedAgeMs < 0 || receivedAgeMs > BLOCKMETA_STREAM_MAX_AGE_MS) {
      return { usable: false, reason: 'stream_too_old', snapshot };
    }
    return { usable: true, snapshot };
  }

  private cacheStreamSnapshot(
    snapshot: AcceptedBlockmetaSnapshot,
    cachedAtMs: number,
  ): RecentBlockhash {
    const resolved: RecentBlockhash = {
      blockhash: snapshot.blockhash,
      source: 'stream',
      reason: 'hit',
      commitment: BLOCKHASH_STREAM_COMMITMENT,
      cachedAtMs,
      chainTimestampSec: snapshot.timestamp,
    };
    this.localCache = resolved;
    return resolved;
  }

  private isUsableLocalCache(cached: RecentBlockhash, nowMs: number): boolean {
    return nowMs - cached.cachedAtMs <= BLOCKHASH_LOCAL_CACHE_TTL_MS && cached.source !== 'stream';
  }

  private observeStreamState(nowMs: number): void {
    const nowMonoMs = this.monotonicNow();
    const stream = this.usableStreamSnapshot(nowMonoMs);
    if (stream.usable) {
      this.recordSource('stream', 'observe_mode', {
        commitment: BLOCKHASH_STREAM_COMMITMENT,
        cachedAtMs: nowMs,
        chainTimestampSec: stream.snapshot.timestamp,
      });
      return;
    }
    if (stream.reason !== 'missing') {
      this.logStreamFallback(stream.reason, stream.snapshot, nowMonoMs);
    }
  }

  private logStreamFallback(
    reason: FallbackReason,
    snapshot: AcceptedBlockmetaSnapshot | null,
    nowMonoMs: number,
    context?: BlockhashResolutionContext,
  ): void {
    if (reason === 'missing') return;
    const message =
      reason === 'stream_too_old'
        ? 'blockchain.blockmeta.stream_too_old'
        : 'blockchain.blockmeta.stream_invalid';
    this.logger.warn(message, {
      commitment: BLOCKHASH_STREAM_COMMITMENT,
      reason,
      ...(isValidTransactionTraceId(context?.traceId) ? { traceId: context.traceId } : {}),
      ...(snapshot !== null
        ? {
            ageMs: String(nowMonoMs - snapshot.receivedAtMonoMs),
            maxAgeMs: String(BLOCKMETA_STREAM_MAX_AGE_MS),
            timestamp: String(snapshot.timestamp),
          }
        : {}),
    });
  }

  private recordSource(
    source: RecentBlockhash['source'] | 'rpc_failure',
    reason: FallbackReason,
    context: {
      readonly commitment?: RpcCommitment;
      readonly cachedAtMs?: number;
      readonly chainTimestampSec?: number;
    },
  ): void {
    this.logger.info('blockchain.blockhash.source', {
      commitment: context.commitment ?? BLOCKHASH_STREAM_COMMITMENT,
      source,
      reason,
      ...(context.chainTimestampSec !== undefined
        ? { timestamp: String(context.chainTimestampSec) }
        : {}),
    });
  }

  private recordRpcFailure(
    reason: FallbackReason,
    err: unknown,
    context?: BlockhashResolutionContext,
  ): void {
    this.recordSource('rpc_failure', 'rpc_failure', { commitment: this.rpcCommitment });
    const traceId = isValidTransactionTraceId(context?.traceId) ? context.traceId : undefined;
    if (traceId !== undefined && context?.suppressRpcFallbackWarning === true) {
      return;
    }
    this.logger.warn('blockchain.blockhash.rpc_fallback_failed', {
      commitment: this.rpcCommitment,
      reason,
      errorClass: err instanceof Error ? 'Error' : typeof err,
      ...(traceId === undefined ? {} : { traceId }),
    });
  }
}
